/* Texturlisten für die Seitenleiste: welche Texturen die Karte benutzt, was fehlt, welche WADs, Sprites und Sounds erwartet werden.
 * Reine Funktionen ohne DOM. */
import { modelPaths, spritePaths } from './entvisual.ts';
import type { Doc } from './types.ts';

/** Eine benutzte Textur: Name, Anzahl der Flächen und ob sie verfügbar ist (eingebettet oder in einer geladenen WAD) */
export interface UsedTex { name: string; n: number; ok: boolean }

/** Texturen der Karte mit Anzahl der Flächen, häufigste zuerst */
export function countTextures(doc: Doc): [string, number][] {
  const m = new Map<string, number>();
  doc.ents.forEach((e) => e.brushes.forEach((b) => b.faces.forEach((f) => { m.set(f.tex, (m.get(f.tex) || 0) + 1); })));
  return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
}

/** Auszug aus einer geladenen BSP: Flächen und Texturen */
export interface BspLike { polys: { tex: string; special: boolean }[]; textures: { name: string; rgba: unknown }[] }

/**
 * Benutzte Texturen inklusive BSP-Flächen; fehlende zuerst, dann nach Häufigkeit.
 * Bei einer geladenen BSP zählen deren Flächen (Spezialflächen ohne Namen nicht), eingebettete Texturen gelten als vorhanden.
 */
export function usedInfo(doc: Doc, bsp: BspLike | null, has: (name: string) => boolean): UsedTex[] {
  let list: [string, number][];
  if (bsp) {
    const m = new Map<string, number>();
    bsp.polys.forEach((q) => { if (q.special && !q.tex) return; m.set(q.tex, (m.get(q.tex) || 0) + 1); });
    list = Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  } else list = countTextures(doc);
  const emb = new Set(bsp ? bsp.textures.filter((t) => t.rgba).map((t) => t.name.toUpperCase()) : []);
  return list.map((u): UsedTex => ({ name: u[0], n: u[1], ok: emb.has(String(u[0]).toUpperCase()) || has(u[0]) }))
    .sort((a, b) => (Number(a.ok) - Number(b.ok)) || (b.n - a.n));
}

/** Dateinamen aus dem wad-Eintrag der Welt: "\valve\halflife.wad;\valve\sdhlt.wad" → ["halflife.wad", "sdhlt.wad"] */
export function expectedWads(wadKv: string | undefined): string[] {
  return String(wadKv || '').split(';').map((s) => s.trim().split(/[\\/]/).pop() || '').filter(Boolean);
}

/** Ist die WAD geladen? loaded enthält Einträge der Form "datei.wad (Anzahl)" */
export function wadLoaded(loaded: string[], name: string): boolean {
  return loaded.some((x) => x.replace(/ \(\d+\)$/, '').toLowerCase() === name.toLowerCase());
}

/** Wie oft die Karte eine Datei braucht (Sprites aus env_sprite/env_glow/cycler_sprite, Sounds aus ambient_generic); keyOf normalisiert den Namen */
export function needs(doc: Doc, classes: RegExp, field: string, keyOf: (v: string) => string): Map<string, number> {
  const m = new Map<string, number>();
  doc.ents.forEach((e) => { if (classes.test(e.kv.classname) && e.kv[field]) { const k = keyOf(e.kv[field]); m.set(k, (m.get(k) || 0) + 1); } });
  return m;
}
export const spriteNeeds = (doc: Doc, keyOf: (v: string) => string): Map<string, number> => spritePaths(doc.ents, keyOf);
/** Modelle, die die Karte über ihre Entity-Klassen (FGD) und das Feld model braucht */
export const modelNeeds = (doc: Doc, keyOf: (v: string) => string): Map<string, number> => modelPaths(doc.ents, keyOf);
export const soundNeeds = (doc: Doc, keyOf: (v: string) => string): Map<string, number> => needs(doc, /^ambient_generic$/, 'message', keyOf);

/** Zusammenfassung der Texturen einer BSP: eingebettet, in einer geladenen WAD, fehlend */
export function bspTexSummary(textures: { name: string; rgba: unknown }[], has: (name: string) => boolean): { total: number; emb: number; wad: number; miss: number; missNames: string[] } {
  const tl = textures.filter((t) => t.name), emb = tl.filter((t) => t.rgba), inW = tl.filter((t) => !t.rgba && has(t.name)), miss = tl.filter((t) => !t.rgba && !has(t.name));
  return { total: tl.length, emb: emb.length, wad: inW.length, miss: miss.length, missNames: miss.map((t) => t.name) };
}

/** Alle Texturen der geladenen WADs: benutzte zuerst, danach nach Name; das Muster filtert (Teilstring, ohne Groß-/Kleinschreibung) */
export function filterTextures(names: string[], used: Set<string>, pattern: string): string[] {
  const f = pattern.trim().toUpperCase();
  const sorted = names.slice().sort((x, y) => (Number(used.has(y.toUpperCase())) - Number(used.has(x.toUpperCase()))));
  return sorted.filter((n) => !f || n.toUpperCase().indexOf(f) >= 0);
}
